import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { deleteNetwork, getNetwork, listNetworks, registerNetwork, updateNetwork } from './api/api'
import { networkDetailKey, networksListKey } from './keyFactory'
import type { NetworkInput } from './types'

export function useNetworks() {
  return useQuery({ queryKey: networksListKey(), queryFn: listNetworks })
}

export function useNetwork(id: string) {
  return useQuery({ queryKey: networkDetailKey(id), queryFn: () => getNetwork(id), enabled: !!id })
}

// All mutations invalidate the list (plan.md §3.4).
export function useRegisterNetwork() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NetworkInput) => registerNetwork(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: networksListKey() }),
  })
}

export function useUpdateNetwork() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: NetworkInput }) => updateNetwork(id, input),
    onSuccess: (network) => {
      queryClient.invalidateQueries({ queryKey: networksListKey() })
      queryClient.invalidateQueries({ queryKey: networkDetailKey(network.id) })
    },
  })
}

export function useDeleteNetwork() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteNetwork(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: networksListKey() }),
  })
}
